/**
 * Pick a colleague out of the employee directory by typing their name.
 *
 * ## Why this exists
 *
 * Ivanti hands "User name" over as an ordinary pick list, and the connector can
 * only page in the first couple of hundred rows — so the field rendered as a
 * dropdown of 201 entries labelled with raw RecIDs, out of 1,663 employees.
 * Most colleagues were unreachable and the ones that were listed were
 * unreadable. This replaces the dropdown with a search: the user types, the
 * server matches, and the id that gets submitted is never shown.
 *
 * ## Why `Autocomplete` and not `Combobox`
 *
 * Both design-system components can do externally-controlled filtering, which
 * is the non-negotiable part of an async search (`filter={null}` hands the
 * matching to the server). `Combobox` models the selection more literally —
 * its `value` is the chosen record and its input text is separate — but it
 * ships no styled control wrapper, so composing input + spinner + clear +
 * trigger means hand-rolling the box the DS already draws, and the DS's own
 * story does it with inline styles this repo forbids. `Autocomplete` ships
 * `Autocomplete.Control` styled for exactly that row, so the field is the
 * design system's rather than an approximation of it.
 *
 * The trade is that `Autocomplete`'s `value` is the *input text*, not the
 * record — so the selected id is held here and published through `onChange`,
 * captured from `Autocomplete.Item`'s `onClick` (which fires on Enter as well
 * as on click). From the outside the component's value is still the employee
 * id, which is what the form submits.
 *
 * ## What it does about a directory too big to list
 *
 * Nothing is fetched until the query is worth sending (see
 * `DIRECTORY_MIN_QUERY_LENGTH`), the query is debounced, and previous results
 * stay on screen while the next ones load so the list does not blink its empty
 * state between keystrokes. When the server says it truncated the list, the
 * popup says so — a silently cut list is how you convince someone their
 * colleague does not exist.
 *
 * ## Tall enough to see that it scrolls (8 Oct)
 *
 * The design system's popup is 20rem with a hairline scrollbar; with two-line
 * rows that is five people and no sign of a sixth. On LIVE "daniel" matched
 * twelve and Rusty saw five. The popup is now about eight rows tall (capped
 * by the space Base UI says is available), the scrollbar is a real one, and
 * a footer counts the matches — see `pickerFooter`.
 *
 * ## Close spellings (16 Oct)
 *
 * When nobody matches as typed, the server adds close spellings flagged
 * `close_match` ("Rogers" -> Rodgers, "Juergen" -> Jürgen). They are listed
 * as usual and the footer says they are suggestions, not exact matches.
 */

import { ChevronDown, Icon, X } from "@norican/icons";
import { Autocomplete, cn, Spinner, Typography } from "@norican/react";
import { useEffect, useMemo, useRef, useState } from "react";

import { pickerFooter } from "@/features/tickets/lib/employee-picker-footer";
import { useDebouncedValue } from "@/hooks";
import { useT } from "@/i18n";
import { DIRECTORY_MIN_QUERY_LENGTH, type DirectoryEntry, useDirectoryEmployees } from "@/lib/api";

/** How long the field waits after the last keystroke before searching. */
const SEARCH_DEBOUNCE_MS = 250;

/** How many matches to ask for. Beyond this the server sets `truncated`. */
const SEARCH_LIMIT = 20;

/**
 * The line under a name that tells two Anders Nielsens apart.
 *
 * Job title first because it is the most human, then department, then email as
 * the guaranteed-unique fallback. Null when the directory knows none of them,
 * in which case the row is just the name.
 */
function secondaryDetail(employee: DirectoryEntry): string | null {
  // Ivanti returns an unset column as `""` about as often as it returns null,
  // so `??` alone would happily pick an empty job title over a real email.
  for (const candidate of [employee.job_title, employee.department, employee.email]) {
    if (candidate != null && candidate.trim() !== "") {
      return candidate;
    }
  }
  return null;
}

/**
 * A stand-in for an employee we hold an id for but have not searched up.
 *
 * The offering can default a lookup field to someone (usually the requester),
 * and it sends the name alongside the id precisely so the box does not open
 * showing a GUID. Where even that is missing the id itself is shown — visibly
 * wrong beats invisibly wrong, because the user can then clear it.
 */
function placeholderEmployee(id: string, displayName: string | null): DirectoryEntry {
  return {
    id,
    display_name: displayName ?? id,
    email: null,
    job_title: null,
    department: null,
    manager_name: null,
    manager_email: null,
  };
}

export interface EmployeePickerProps {
  /** The selected employee's RecId, or null for no selection. */
  value: string | null;
  /**
   * Called with the whole record, not just the id.
   *
   * The manager auto-fill needs `manager_name`, and re-fetching a record the
   * user just picked to read one field off it would be silly.
   */
  onChange: (employee: DirectoryEntry | null) => void;
  /** Human name for `value` when it arrived as an offering default. */
  defaultDisplayValue?: string | null;
  /** Suppresses the clear button — a required field cannot be emptied. */
  required?: boolean;
  disabled?: boolean;
  name?: string;
  placeholder?: string;
  "aria-label"?: string;
  className?: string;
  /**
   * Told whether a name is typed in the box that nobody was picked for
   * (7 Oct): the form then says "pick the person from the list" rather than
   * sending the field as if it were answered — or empty.
   */
  onTextPendingChange?: (pending: boolean) => void;
}

export function EmployeePicker({
  value,
  onChange,
  defaultDisplayValue = null,
  required = false,
  disabled = false,
  name,
  placeholder,
  "aria-label": ariaLabel,
  className,
  onTextPendingChange,
}: EmployeePickerProps) {
  const { t } = useT("tickets");

  // The last record actually chosen here. Everything else about the selection
  // is derived, so there is no second copy of `value` to drift from the form's.
  const [chosen, setChosen] = useState<DirectoryEntry | null>(null);

  const selected = useMemo<DirectoryEntry | null>(() => {
    if (value === null || value === "") {
      return null;
    }
    if (chosen !== null && chosen.id === value) {
      return chosen;
    }
    return placeholderEmployee(value, defaultDisplayValue);
  }, [value, chosen, defaultDisplayValue]);

  const selectedLabel = selected?.display_name ?? "";

  // `Autocomplete`'s value is the input text, and it is controlled here so the
  // field can be re-labelled when the selection changes from outside (a form
  // reset, a default arriving late). Syncing during render rather than in an
  // effect keeps the box from painting the stale name for a frame.
  const [inputValue, setInputValue] = useState(selectedLabel);
  const [syncedLabel, setSyncedLabel] = useState(selectedLabel);
  if (syncedLabel !== selectedLabel) {
    setSyncedLabel(selectedLabel);
    setInputValue(selectedLabel);
  }

  // Typed but not picked: text in the box that is not the chosen person's name.
  const textPending = inputValue.trim() !== "" && inputValue !== selectedLabel;
  const onPendingRef = useRef(onTextPendingChange);
  useEffect(() => {
    onPendingRef.current = onTextPendingChange;
  });
  useEffect(() => {
    onPendingRef.current?.(textPending);
  }, [textPending]);

  const debounced = useDebouncedValue(inputValue.trim(), SEARCH_DEBOUNCE_MS);

  // Selecting someone writes their name into the box; that is a selection, not
  // a search, and re-querying it would spend a request to reproduce a list the
  // user has already finished with.
  const query = debounced === selectedLabel ? "" : debounced;
  const tooShort = query.length < DIRECTORY_MIN_QUERY_LENGTH;

  const search = useDirectoryEmployees(query, { limit: SEARCH_LIMIT, enabled: !disabled });
  const items = search.data?.items ?? [];
  const footer = pickerFooter(search.data);
  const searching = search.isFetching;

  const handleInputChange = (next: string) => {
    setInputValue(next);
    // Emptying the box is how you undo a choice — the clear button does the
    // same thing. The selection has to go with the text, or the form would
    // submit an id for a field that visibly reads as blank.
    if (next.trim() === "" && value !== null && value !== "") {
      setChosen(null);
      onChange(null);
    }
  };

  const handleSelect = (employee: DirectoryEntry) => {
    setChosen(employee);
    onChange(employee);
  };

  return (
    <Autocomplete.Root
      items={items}
      // The server has already matched the query; filtering the results again
      // in the browser would drop rows that matched on email or login.
      filter={null}
      mode="list"
      value={inputValue}
      onValueChange={handleInputChange}
      itemToStringValue={(employee: DirectoryEntry) => employee.display_name}
      disabled={disabled}
      required={required}
      name={name}
      openOnInputClick
      autoHighlight
    >
      <Autocomplete.Control className={cn("w-full", className)}>
        <Autocomplete.Input
          placeholder={placeholder ?? t("employeePicker.placeholder")}
          aria-label={ariaLabel}
          autoComplete="off"
        />
        {searching ? <Spinner size="sm" label={t("employeePicker.searching")} /> : null}
        {required ? null : (
          <Autocomplete.Clear aria-label={t("employeePicker.clear")}>
            <Icon icon={X} size="sm" />
          </Autocomplete.Clear>
        )}
        <Autocomplete.Trigger aria-label={t("employeePicker.open")}>
          <Autocomplete.Icon>
            <Icon icon={ChevronDown} size="sm" />
          </Autocomplete.Icon>
        </Autocomplete.Trigger>
      </Autocomplete.Control>

      <Autocomplete.Portal>
        <Autocomplete.Positioner sideOffset={4} align="start">
          <Autocomplete.Popup
            width="anchor"
            // ~8 two-line rows plus the footer, never taller than the room
            // Base UI measured below (or above) the field.
            className="max-h-[min(28rem,var(--available-height,28rem))]"
          >
            <Autocomplete.List className="[scrollbar-width:auto] [scrollbar-color:var(--norican-fg-subtle)_transparent]">
              {(employee: DirectoryEntry) => {
                const detail = secondaryDetail(employee);
                return (
                  <Autocomplete.Item
                    key={employee.id}
                    value={employee}
                    onClick={() => handleSelect(employee)}
                  >
                    <span className="flex min-w-0 flex-col">
                      <Typography as="span" variant="body-sm">
                        {employee.display_name}
                      </Typography>
                      {detail === null ? null : (
                        <Typography as="span" variant="caption" tone="muted">
                          {detail}
                        </Typography>
                      )}
                    </span>
                  </Autocomplete.Item>
                );
              }}
            </Autocomplete.List>

            {/* `Empty` is a live region and has to stay mounted to announce
                reliably, so the three no-results states swap its children
                rather than swapping the element. */}
            <Autocomplete.Empty>
              <Typography variant="body-sm" tone="muted">
                {searching
                  ? t("employeePicker.searching")
                  : tooShort
                    ? t("employeePicker.prompt", { min: DIRECTORY_MIN_QUERY_LENGTH })
                    : search.isError
                      ? t("employeePicker.error")
                      : t("employeePicker.empty")}
              </Typography>
            </Autocomplete.Empty>

            {/* Polite live region: a screen reader hears "Showing 20 of 37"
                once the list settles, without stealing focus from the input. */}
            <div aria-live="polite" className="shrink-0">
              {footer === null ? null : (
                <Typography variant="caption" tone="muted" className="border-t px-4 pt-1 pb-2">
                  {footer.kind === "close"
                    ? t("employeePicker.close")
                    : footer.kind === "scroll"
                      ? t("employeePicker.scroll", { count: footer.count })
                      : footer.total === null
                        ? t("employeePicker.truncated")
                        : t("employeePicker.showing", {
                            shown: footer.shown,
                            total: footer.total,
                          })}
                </Typography>
              )}
            </div>
          </Autocomplete.Popup>
        </Autocomplete.Positioner>
      </Autocomplete.Portal>
    </Autocomplete.Root>
  );
}
